import { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Package, Search, FileText, Users, Truck, Calendar, FolderOpen } from 'lucide-react'
import Button from './Button'

export interface EmptyStateProps {
  icon?: ReactNode
  iconType?: 'products' | 'search' | 'documents' | 'users' | 'shipping' | 'tasks' | 'folder'
  title: string
  description?: string
  action?: {
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary'
  }
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  className?: string
}

const iconMap = {
  products: Package,
  search: Search,
  documents: FileText,
  users: Users,
  shipping: Truck,
  tasks: Calendar,
  folder: FolderOpen,
}

export default function EmptyState({
  icon,
  iconType = 'folder',
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  const IconComponent = iconMap[iconType]

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-6 text-center',
        className
      )}
    >
      {/* Icon */}
      <div className="mb-4">
        {icon || (
          <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <IconComponent className="w-8 h-8 text-slate-400 dark:text-slate-500" />
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6">
          {description}
        </p>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="flex items-center gap-3">
          {action && (
            <Button
              variant={action.variant || 'primary'}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

// Pre-built empty states for common scenarios
export function NoProductsEmptyState({ onAction }: { onAction?: () => void }) {
  return (
    <EmptyState
      iconType="products"
      title="No hay productos"
      description="Importa un catalogo CSV para empezar a gestionar productos de este proveedor"
      action={onAction ? { label: 'Importar CSV', onClick: onAction } : undefined}
    />
  )
}

export function NoSearchResultsEmptyState({ query }: { query: string }) {
  return (
    <EmptyState
      iconType="search"
      title="Sin resultados"
      description={`No se encontraron resultados para "${query}". Prueba con otros terminos de busqueda.`}
    />
  )
}

export function NoCustomersEmptyState({ onAction }: { onAction?: () => void }) {
  return (
    <EmptyState
      iconType="users"
      title="No hay clientes"
      description="Los clientes apareceran aqui cuando se sincronicen desde Factusol"
      action={onAction ? { label: 'Sincronizar', onClick: onAction } : undefined}
    />
  )
}

export function NoTasksEmptyState({ onAction }: { onAction?: () => void }) {
  return (
    <EmptyState
      iconType="tasks"
      title="No hay tareas programadas"
      description="Crea una tarea programada para automatizar procesos"
      action={onAction ? { label: 'Crear tarea', onClick: onAction } : undefined}
    />
  )
}
